import { Injectable } from '@angular/core';
import { LoggerService } from '../../services/logger.service';
import { TemplateService } from '../../car-wash-website/services/template.service';
import { TemplatePopulatorService } from './template-populator.service';
import { WebsiteElement } from '@way/website-themes/themes/minimal_website/home-page-preview/home-page-preview.component';
import { BrandingData } from '../components/branding-panel/branding-panel.component';
import { FooterData } from '../components/footer-panel/footer-panel.component';
import { HeaderData } from '../components/header-panel/header-panel.component';

/**
 * Mutable context bag for branding operations.
 */
export interface BrandingContext {
  websiteId: string | null;
  website: any;
  brandingData: BrandingData;
  elements: WebsiteElement[];
  json: any;
  footerData: FooterData;
  headerData: HeaderData;
  primaryColor: string | null;
  headingColor: string | null;
  paragraphColor: string | null;
  headingFont: string | null;
  bodyFont: string | null;
  showBrandingPanel: boolean;
  populateHeaderFromTemplate(): void;
  populateFooterFromTemplate(): void;
  scheduleAutosave(paths: string[]): void;
}

/**
 * Handles heavy branding update operations: instant logo upload,
 * API updates, and propagating brand colours/logo into page elements.
 */
@Injectable({ providedIn: 'root' })
export class BrandingManagerService {

  constructor(
    private logger: LoggerService,
    private templateService: TemplateService,
    private templatePopulator: TemplatePopulatorService
  ) {}
  /**
  * Immediately applies an instant branding change for a website logo or color update and syncs the preview and API state.
  * @example
  * nInstantBrandingUpdate({ type: 'logo', data: { logo: file } }, ctx)
  * void
  * @param {{ type: 'logo' | 'color', data: Partial<BrandingData> }} update - Branding update payload containing the change type and partial branding data.
  * @param {BrandingContext} ctx - Current branding context used to update the website state, preview data, and API.
  * @returns {void} Nothing is returned.
  **/
  onInstantBrandingUpdate(update: { type: 'logo' | 'color', data: Partial<BrandingData> }, ctx: BrandingContext): void {
    this.logger.debug('Instant branding update:', update);

    if (!ctx.websiteId) {
      this.logger.error('No website ID available for instant branding update');
      return;
    }

    if (update.type === 'logo' && update.data.logo) {
      // Upload logo immediately
      this.templateService.uploadWebsiteFile(update.data.logo).subscribe({
        next: (uploadResponse) => {
          this.logger.debug('Logo uploaded instantly:', uploadResponse);
          const logoUrl = uploadResponse?.data?.url ||
            uploadResponse?.data?.file ||
            uploadResponse?.data?.file_url ||
            uploadResponse?.url ||
            uploadResponse?.file ||
            uploadResponse?.file_url ||
            (uploadResponse?.data && typeof uploadResponse.data === 'string' ? uploadResponse.data : null);

          if (logoUrl) {
            // Update branding data immediately
            ctx.brandingData.logoUrl = logoUrl;
            ctx.brandingData.logo = update.data.logo;
            
            // Also update website object immediately for real-time preview
            if (ctx.website) {
              if (!ctx.website.json_data) {
                ctx.website.json_data = {};
              }
              if (!ctx.website.json_data.website) {
                ctx.website.json_data.website = {};
              }
              if (!ctx.website.json_data.website.branding) {
                ctx.website.json_data.website.branding = {};
              }
              ctx.website.json_data.website.branding.logo = { url: logoUrl };
              
              // Also update header logo if header exists
              if (!ctx.website.json_data.website.header) {
                ctx.website.json_data.website.header = {};
              }
              if (!ctx.website.json_data.website.header.logo) {
                ctx.website.json_data.website.header.logo = {};
              }
              ctx.website.json_data.website.header.logo.url = logoUrl;
              
              // Also update footer logo if footer exists
              if (!ctx.website.json_data.website.footer) {
                ctx.website.json_data.website.footer = {};
              }
              if (!ctx.website.json_data.website.footer.logo) {
                ctx.website.json_data.website.footer.logo = {};
              }
              ctx.website.json_data.website.footer.logo.url = logoUrl;
            }
            
            // Update currentJsonData immediately for preview components
            if (ctx.json) {
              if (!ctx.json.website) {
                ctx.json.website = {};
              }
              if (!ctx.json.website.branding) {
                ctx.json.website.branding = {};
              }
              ctx.json.website.branding.logo = { url: logoUrl };
              
              // Also update header logo in currentJsonData
              if (!ctx.json.website.header) {
                ctx.json.website.header = {};
              }
              if (!ctx.json.website.header.logo) {
                ctx.json.website.header.logo = {};
              }
              ctx.json.website.header.logo.url = logoUrl;
              
              // Also update footer logo in currentJsonData
              if (!ctx.json.website.footer) {
                ctx.json.website.footer = {};
              }
              if (!ctx.json.website.footer.logo) {
                ctx.json.website.footer.logo = {};
              }
              ctx.json.website.footer.logo.url = logoUrl;
            }
            
            // Force change detection for immediate UI update
            ctx.elements = [...ctx.elements];
            ctx.populateHeaderFromTemplate();
            ctx.populateFooterFromTemplate();
            
            // Update API immediately with logo
            this.updateBrandingInAPI({
              ...ctx.brandingData,
              logoUrl: logoUrl
            }, ctx);
          }
        },
        error: (error) => {
          this.logger.error('Error uploading logo instantly:', error);
        }
      });
    } else if (update.type === 'color' && update.data.colors) {
      // Update colors immediately
      ctx.brandingData.colors = { ...ctx.brandingData.colors, ...update.data.colors };
      
      // Update preview immediately
      if (update.data.colors.primary) {
        ctx.primaryColor = update.data.colors.primary;
      }
      if (update.data.colors.heading) {
        ctx.headingColor = update.data.colors.heading;
      }
      if (update.data.colors.paragraph) {
        ctx.paragraphColor = update.data.colors.paragraph;
      }
      
      // Also update website object immediately for real-time preview
      if (ctx.website) {
        if (!ctx.website.json_data) {
          ctx.website.json_data = {};
        }
        if (!ctx.website.json_data.website) {
          ctx.website.json_data.website = {};
        }
        if (!ctx.website.json_data.website.branding) {
          ctx.website.json_data.website.branding = {};
        }
        ctx.website.json_data.website.branding.colors = {
          ...ctx.brandingData.colors,
          ...update.data.colors
        };
      }
      
      // Update currentJsonData immediately if it exists
      if (ctx.json) {
        if (!ctx.json.website) {
          ctx.json.website = {};
        }
        if (!ctx.json.website.branding) {
          ctx.json.website.branding = {};
        }
        ctx.json.website.branding.colors = {
          ...ctx.brandingData.colors,
          ...update.data.colors
        };
      }
      
      // Force change detection for immediate UI update
      ctx.elements = [...ctx.elements];
      
      // Update API immediately with colors only
      this.updateBrandingInAPI({
        ...ctx.brandingData,
        colors: update.data.colors
      }, ctx);
    }
  }

  // Helper method to update branding in API (colors and/or fonts, not logo)
  /**
  * Updates website branding data in the API and synchronizes local website state.
  * @example
  * pdateBrandingInAPI(brandingData, ctx)
  * void
  * @param {BrandingData} brandingData - Branding values such as logo, colors, and fonts to apply.
  * @param {BrandingContext} ctx - Current website context containing the website ID and existing website data.
  * @returns {void} Does not return a value.
  **/
  updateBrandingInAPI(brandingData: BrandingData, ctx: BrandingContext): void {
    if (!ctx.websiteId) return;

    // Get current json_data from website
    const jsonData = ctx.website?.json_data || {};
    
    // Ensure website structure exists
    if (!jsonData.website) {
      jsonData.website = {};
    }
    
    // Update branding data in json_data
    if (!jsonData.website.branding) {
      jsonData.website.branding = {};
    }
    
    // Update logo if provided
    if (brandingData.logoUrl) {
      jsonData.website.branding.logo = { url: brandingData.logoUrl };
      
      // Also update footer logo if footer exists
      if (!jsonData.website.footer) {
        jsonData.website.footer = {};
      }
      if (!jsonData.website.footer.logo) {
        jsonData.website.footer.logo = {};
      }
      jsonData.website.footer.logo.url = brandingData.logoUrl;
      jsonData.website.footer.logo.image = brandingData.logoUrl;
    }
    
    // Update colors
    if (brandingData.colors) {
      jsonData.website.branding.colors = {
        primary: brandingData.colors.primary,
        heading: brandingData.colors.heading,
        paragraph: brandingData.colors.paragraph
      };
    }
    
    // Update fonts (if provided, otherwise keep existing)
    if (brandingData.fonts) {
      jsonData.website.branding.fonts = {
        heading: brandingData.fonts.heading,
        body: brandingData.fonts.body
      };
    }

    // Also update at root level for backward compatibility
    if (!jsonData.branding) {
      jsonData.branding = {};
    }
    if (brandingData.logoUrl) {
      jsonData.branding.logo = { url: brandingData.logoUrl };
    }
    if (brandingData.colors) {
      jsonData.branding.colors = brandingData.colors;
    }
    if (brandingData.fonts) {
      jsonData.branding.fonts = brandingData.fonts;
    }

    // Create FormData with brand_logo, brand_colors, fonts, header, and footer
    // This matches the API format: PUT /api/builder/website/{websiteId}
    const formData = new FormData();
    
    // Add brand_logo (URL string) if available
    if (brandingData.logoUrl) {
      formData.append('brand_logo', brandingData.logoUrl);
    }
    
    // Add brand_colors as JSON string
    if (brandingData.colors) {
      const brandColorsJson = JSON.stringify({
        primary: brandingData.colors.primary,
        heading: brandingData.colors.heading,
        paragraph: brandingData.colors.paragraph
      });
      formData.append('brand_colors', brandColorsJson);
    }
    
    // Add fonts as JSON string
    if (brandingData.fonts) {
      const fontsJson = JSON.stringify({
        heading: brandingData.fonts.heading,
        body: brandingData.fonts.body
      });
      formData.append('fonts', fontsJson);
    }
    
    // Add header as JSON string (if it exists in jsonData)
    if (jsonData.website?.header) {
      formData.append('header', JSON.stringify(jsonData.website.header));
    }
    
    // Add footer as JSON string (if it exists in jsonData)
    if (jsonData.website?.footer) {
      formData.append('footer', JSON.stringify(jsonData.website.footer));
    }
    
    // Also append json_data for backward compatibility
    formData.append('json_data', JSON.stringify(jsonData));

    // Update website via API using FormData
    this.templateService.updateWebsite(ctx.websiteId, formData).subscribe({
      next: (response) => {
        this.logger.debug('Branding updated instantly in API:', response);
        // Update local website data
        if (response?.data) {
          ctx.website = response.data;
          ctx.json = response.data.json_data || {};
        }
      },
      error: (error) => {
        this.logger.error('Error updating branding instantly:', error);
      }
    });
  }

  /**
  * Applies branding updates to a website, including optional logo upload, colors, and fonts.
  * @example
  * nApplyBranding(brandingData, ctx)
  * undefined
  * @param {BrandingData} brandingData - Branding information such as logo, colors, and font settings.
  * @param {BrandingContext} ctx - Context containing website ID and branding panel state.
  * @returns {void} No return value.
  **/
  onApplyBranding(brandingData: BrandingData, ctx: BrandingContext): void {
    this.logger.debug('onApplyBranding called with:', brandingData);
    this.logger.debug('Website ID:', ctx.websiteId);

    if (!ctx.websiteId) {
      this.logger.error('No website ID available for updating branding');
      ctx.showBrandingPanel = false;
      return;
    }

    // If logo file is present, upload it first to get the URL
    if (brandingData.logo) {
      this.logger.debug('Uploading logo file:', { name: brandingData.logo.name, size: brandingData.logo.size });

      this.templateService.uploadWebsiteFile(brandingData.logo).subscribe({
        next: (uploadResponse) => {
          this.logger.debug('Logo uploaded successfully:', uploadResponse);

          // Get logo URL from response - check multiple possible response structures
          const logoUrl = uploadResponse?.data?.url ||
            uploadResponse?.data?.file ||
            uploadResponse?.data?.file_url ||
            uploadResponse?.url ||
            uploadResponse?.file ||
            uploadResponse?.file_url ||
            (uploadResponse?.data && typeof uploadResponse.data === 'string' ? uploadResponse.data : null);

          if (logoUrl) {
            this.logger.debug('Logo URL extracted:', logoUrl);
            // Update the website with branding (logo, colors, fonts)
            this.updatePageWithBrandLogo(logoUrl, brandingData, ctx);
          } else {
            this.logger.error('Logo URL not found in upload response. Full response:', uploadResponse);
            // Still update colors and fonts even if logo upload failed
            this.updatePageWithBrandLogo(brandingData.logoUrl || '', brandingData, ctx);
          }
        },
        error: (error) => {
          this.logger.error('Error uploading logo:', error);
          this.logger.error('Error details:', JSON.stringify(error, null, 2));
          // Still update colors and fonts even if logo upload failed
          this.updatePageWithBrandLogo(brandingData.logoUrl || '', brandingData, ctx);
        }
      });
    } else {
      // No new logo file, but may have colors/fonts to update
      // Use existing logoUrl if available, or empty string
      const logoUrl = brandingData.logoUrl || ctx.brandingData.logoUrl || '';
      this.updatePageWithBrandLogo(logoUrl, brandingData, ctx);
    }
  }

  /**
   * Updates the website branding with a logo, colors, and fonts, syncs the preview state, and persists the changes through the website update API.
   * @example
   * pdatePageWithBrandLogo(logoUrl, brandingData, ctx)
   * void
   * @param {string} logoUrl - The URL of the brand logo to apply to the website.
   * @param {BrandingData} brandingData - Branding details containing optional logo, colors, and font settings.
   * @param {BrandingContext} ctx - The branding context used to update website state, preview values, and UI visibility.
   * @returns {void} Nothing is returned.
   **/
  updatePageWithBrandLogo(logoUrl: string, brandingData: BrandingData, ctx: BrandingContext): void {
    if (!ctx.websiteId) {
      this.logger.error('Missing website ID for updating brand logo');
      ctx.showBrandingPanel = false;
      return;
    }

    // Check if this is a font-only update (no logo file, no new logo URL, no color changes)
    const currentColors = (ctx.brandingData.colors || {}) as any;
    const newColors = (brandingData.colors || {}) as any;
    const colorsUnchanged = 
      currentColors.primary === newColors.primary &&
      currentColors.heading === newColors.heading &&
      currentColors.paragraph === newColors.paragraph;
    
    const isFontOnlyUpdate = !brandingData.logo && 
                            !logoUrl && 
                            colorsUnchanged &&
                            (brandingData.fonts?.heading || brandingData.fonts?.body);

    this.logger.debug('Updating website branding:', { logoUrl, colors: brandingData.colors, fonts: brandingData.fonts, isFontOnlyUpdate });
    this.logger.debug('Website ID:', ctx.websiteId);

    // Get current json_data from website
    const jsonData = ctx.website?.json_data || {};
    
    // Ensure website structure exists
    if (!jsonData.website) {
      jsonData.website = {};
    }
    
    // Update branding data in json_data
    if (!jsonData.website.branding) {
      jsonData.website.branding = {};
    }
    
    // Update logo
    if (logoUrl) {
      jsonData.website.branding.logo = { url: logoUrl };
      
      // Also update footer logo if footer exists
      if (!jsonData.website.footer) {
        jsonData.website.footer = {};
      }
      if (!jsonData.website.footer.logo) {
        jsonData.website.footer.logo = {};
      }
      jsonData.website.footer.logo.url = logoUrl;
      jsonData.website.footer.logo.image = logoUrl;
    }
    
    // Update colors
    if (brandingData.colors) {
      jsonData.website.branding.colors = {
        primary: brandingData.colors.primary,
        heading: brandingData.colors.heading,
        paragraph: brandingData.colors.paragraph
      };
      
      // Immediately update primary color for real-time preview
      if (brandingData.colors.primary) {
        ctx.primaryColor = brandingData.colors.primary;
      }
      
      // Immediately update heading color for real-time preview
      if (brandingData.colors.heading) {
        ctx.headingColor = brandingData.colors.heading;
      }
      
      // Immediately update paragraph color for real-time preview
      if (brandingData.colors.paragraph) {
        ctx.paragraphColor = brandingData.colors.paragraph;
      }
      
      // Immediately update fonts for real-time preview
      if (brandingData.fonts?.heading) {
        ctx.headingFont = brandingData.fonts.heading;
      }
      
      if (brandingData.fonts?.body) {
        ctx.bodyFont = brandingData.fonts.body;
      }
    }
    
    // Update fonts
    if (brandingData.fonts) {
      jsonData.website.branding.fonts = {
        heading: brandingData.fonts.heading,
        body: brandingData.fonts.body
      };
    }

    // Also update at root level for backward compatibility
    if (!jsonData.branding) {
      jsonData.branding = {};
    }
    if (logoUrl) {
      jsonData.branding.logo = { url: logoUrl };
    }
    if (brandingData.colors) {
      jsonData.branding.colors = brandingData.colors;
    }
    if (brandingData.fonts) {
      jsonData.branding.fonts = brandingData.fonts;
    }

    // Create FormData with brand_logo, brand_colors, fonts, header, and footer
    // This matches the API format: PUT /api/builder/website/{websiteId}
    const formData = new FormData();
    
    // Add brand_logo (URL string)
    if (logoUrl) {
      formData.append('brand_logo', logoUrl);
    }
    
    // Add brand_colors as JSON string
    if (brandingData.colors) {
      const brandColorsJson = JSON.stringify({
        primary: brandingData.colors.primary,
        heading: brandingData.colors.heading,
        paragraph: brandingData.colors.paragraph
      });
      formData.append('brand_colors', brandColorsJson);
    }
    
    // Add fonts as JSON string
    if (brandingData.fonts) {
      const fontsJson = JSON.stringify({
        heading: brandingData.fonts.heading,
        body: brandingData.fonts.body
      });
      formData.append('fonts', fontsJson);
    }
    
    // Add header as JSON string (if it exists in jsonData)
    if (jsonData.website?.header) {
      formData.append('header', JSON.stringify(jsonData.website.header));
    }
    
    // Add footer as JSON string (if it exists in jsonData)
    if (jsonData.website?.footer) {
      formData.append('footer', JSON.stringify(jsonData.website.footer));
    }
    
    // Also append json_data for backward compatibility
    formData.append('json_data', JSON.stringify(jsonData));
    
    // Update primary color and website object immediately for real-time preview (before API call)
    if (brandingData.colors?.primary) {
      ctx.primaryColor = brandingData.colors.primary;
    }
    
    // Update website object immediately with new json_data for real-time preview
    if (ctx.website) {
      ctx.website.json_data = jsonData;
    }
    
    // Update currentJsonData immediately if it exists to reflect branding changes
    if (ctx.json) {
      // Ensure website structure exists in currentJsonData
      if (!ctx.json.website) {
        ctx.json.website = {};
      }
      if (!ctx.json.website.branding) {
        ctx.json.website.branding = {};
      }
      
      // Update branding data in website structure (matching API format)
      if (logoUrl) {
        if (!ctx.json.website.branding.logo) {
          ctx.json.website.branding.logo = {};
        }
        ctx.json.website.branding.logo.url = logoUrl;
      }
      if (brandingData.colors) {
        ctx.json.website.branding.colors = {
          primary: brandingData.colors.primary,
          heading: brandingData.colors.heading,
          paragraph: brandingData.colors.paragraph
        };
      }
      if (brandingData.fonts) {
        ctx.json.website.branding.fonts = {
          heading: brandingData.fonts.heading,
          body: brandingData.fonts.body
        };
      }
    }
    
    // Force change detection by creating new array reference for immediate UI update
    ctx.elements = [...ctx.elements];

    this.logger.debug('FormData being sent:');
    this.logger.debug('  brand_logo:', logoUrl);
    if (brandingData.colors) {
      this.logger.debug('  brand_colors:', JSON.stringify(brandingData.colors));
    }
    if (brandingData.fonts) {
      this.logger.debug('  fonts:', JSON.stringify(brandingData.fonts));
    }
    if (jsonData.website?.header) {
      this.logger.debug('  header:', JSON.stringify(jsonData.website.header));
    }
    if (jsonData.website?.footer) {
      this.logger.debug('  footer:', JSON.stringify(jsonData.website.footer));
    }
    this.logger.debug('  json_data:', JSON.stringify(jsonData, null, 2));

    // Update the website with branding data using updateWebsite API
    this.templateService.updateWebsite(ctx.websiteId, formData).subscribe({
      next: (response) => {
        this.logger.debug('Website branding updated successfully:', response);

        // Update local branding data
        ctx.brandingData = {
          ...brandingData,
          logoUrl: logoUrl || ctx.brandingData.logoUrl
        };

        // Note: primaryColor, website.json_data, and currentJsonData are already updated
        // before the API call for immediate preview, so we don't need to update them again here
        
        // Refresh website object from API response if available (in case of any server-side changes)
        if (response?.data?.website) {
          ctx.website = response.data.website;
          // Ensure primary color is still set from branding
          if (ctx.website?.json_data?.website?.branding?.colors?.primary) {
            ctx.primaryColor = ctx.website.json_data.website.branding.colors.primary;
          }
        } else if (response?.data) {
          ctx.website = response.data;
          if (ctx.website?.json_data?.website?.branding?.colors?.primary) {
            ctx.primaryColor = ctx.website.json_data.website.branding.colors.primary;
          }
        }

        // Repopulate header and footer to immediately show the updated logo
        ctx.populateHeaderFromTemplate();
        ctx.populateFooterFromTemplate();

        // Force change detection by creating new array reference to trigger preview updates
        // This ensures all preview components get the updated primary color immediately
        ctx.elements = [...ctx.elements];

        // Only close panel if this is NOT a font-only update
        // Font-only updates should keep the panel open so user can see the change
        if (!isFontOnlyUpdate) {
          ctx.showBrandingPanel = false;
        }
      },
      error: (error) => {
        this.logger.error('Error updating website branding:', error);
        this.logger.error('Error details:', JSON.stringify(error, null, 2));
        // Still close the panel even if there's an error (unless it's font-only)
        ctx.brandingData = brandingData;
        if (!isFontOnlyUpdate) {
          ctx.showBrandingPanel = false;
        }
      }
    });
  }

}